import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import WiseRecipientModal from "../affiliate-details/WiseRecipientModal";
import Button from "../ui/Button";
import { CreditCard, Plus } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { wiseRecipientService } from "../../lib/wiseRecipientService";
import Card from "../ui/Card";
import BankNoticeModal from "../ui/BankNoticeModal";
import { getCanonicalAffiliateName } from "../../lib/affiliateIdentity";

const BankAccountsPage = () => {
  const { affiliateName } = useParams();
  const decodedAffiliateName = decodeURIComponent(affiliateName);
  const { user, profile, isAdmin } = useAuth();
  const canonicalAffiliateName = getCanonicalAffiliateName(profile, user);
  const effectiveAffiliateName = isAdmin
    ? decodedAffiliateName
    : canonicalAffiliateName;
  const [showWiseModal, setShowWiseModal] = useState(false);

  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRecipients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await wiseRecipientService.getRecipients(
        effectiveAffiliateName
      );
      if (result.success) {
        setRecipients(result.data || []);
      } else {
        setError("Failed to load recipients: " + result.error);
      }
    } catch (err) {
      setError("Failed to load recipients.");
    } finally {
      setLoading(false);
    }
  }, [effectiveAffiliateName]);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  return (
    <div className="space-y-6">
      {/* Admin-controlled Bank Notice Modal */}
      <BankNoticeModal />

      {/* Page Title */}
      <div className="mb-6">
        <p className="text-lg text-gray-300">Manage Your Bank Accounts</p>
      </div>

      {/* Bank Account Management Section */}
      <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#B8460E]/20 rounded-xl flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-[#B8460E]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Bank Accounts</h2>
              <p className="text-gray-400 text-sm">
                Add and manage your payout accounts
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowWiseModal(true)}
            className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white px-4 py-2 rounded-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Bank Account
          </Button>
        </div>

        {/* Recipients list or empty state */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">{error}</div>
        ) : recipients.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-white/50" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              No Bank Accounts Yet
            </h3>
            <p className="text-gray-400 mb-4">
              Add your bank account information to receive payouts via Wise
            </p>
            <Button
              onClick={() => setShowWiseModal(true)}
              className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105"
            >
              Add Your First Account
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {recipients.map((recipient) => (
              <Card
                key={recipient.id}
                className="p-4 bg-white/5 backdrop-blur-2xl border border-white/10 shadow-xl flex flex-col md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <div className="font-semibold text-white">
                    {recipient.accountHolderName}
                  </div>
                  <div className="text-sm text-gray-400">
                    {recipient.currency} • {recipient.details?.address?.country}
                  </div>
                  {recipient.details?.accountNumber && (
                    <div className="text-xs text-gray-500">
                      Account: ****{recipient.details.accountNumber.slice(-4)}
                    </div>
                  )}
                  {recipient.details?.iban && (
                    <div className="text-xs text-gray-500">
                      IBAN: ****{recipient.details.iban.slice(-4)}
                    </div>
                  )}
                </div>
                <div className="mt-2 md:mt-0">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Wise Recipient Modal */}
      <WiseRecipientModal
        isOpen={showWiseModal}
        onClose={() => setShowWiseModal(false)}
        affiliateName={effectiveAffiliateName}
        onSaved={loadRecipients}
      />
    </div>
  );
};

export default BankAccountsPage;
